import React, { useEffect, useRef, useState, useCallback } from 'react';
import './TradingViewChart.css';

function TradingViewChart({ symbol, onClose }) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(300); // Default to minimum height
  const [isDragging, setIsDragging] = useState(false);
  const isResizing = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);

  // Create widget function
  const createWidget = useCallback((height) => {
    // Check if TradingView is loaded
    if (!window.TradingView) {
      console.error('TradingView library not loaded');
      return;
    }

    // Clean symbol format — default to perpetual futures (USDT.P) since most low-cap coins
    // don't have a spot USDT pair on Binance but almost always have the perp.
    const baseSymbol = symbol.replace(/USDT$|USDC$|USD$/i, '');
    const fullSymbol = `BINANCE:${baseSymbol}USDT.P`;

    console.log(`📊 Initializing TradingView chart for: ${baseSymbol}/USDT`);

    // Clear container
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    // Create new widget with explicit pixel dimensions
    try {
      widgetRef.current = new window.TradingView.widget({
        container_id: containerRef.current.id,
        symbol: fullSymbol,
        interval: '1', // 1-minute candlesticks
        timezone: 'Etc/UTC',
        theme: 'dark',
        style: '1', // Candlestick style
        locale: 'en',
        toolbar_bg: '#1a1a1a',
        enable_publishing: false,
        hide_top_toolbar: false,
        hide_legend: false,
        save_image: false,
        backgroundColor: '#000000',
        gridColor: '#1a1a1a',
        width: '100%',
        height: height,
        studies: [],
        show_popup_button: false,
        popup_width: '1000',
        popup_height: '650',
        autosize: true
      });

      console.log(`✅ TradingView chart loaded: ${baseSymbol}/USDT with height ${height}px`);
    } catch (error) {
      console.error('Error creating TradingView widget:', error);
    }
  }, [symbol]);

  // Initial widget creation on symbol change
  useEffect(() => {
    // Small delay to ensure container is rendered with correct dimensions
    const timeoutId = setTimeout(() => {
      createWidget(chartHeight);
    }, 50);

    // Cleanup on unmount or symbol change.
    // Setting widgetRef = null alone leaks the widget's listeners, timers,
    // and internal state. Call .remove() if the API supports it, then drop
    // the container's DOM children so any retained nodes are detached.
    return () => {
      clearTimeout(timeoutId);
      try {
        if (widgetRef.current && typeof widgetRef.current.remove === 'function') {
          widgetRef.current.remove();
        }
      } catch (err) {
        console.warn('TradingView widget remove() failed:', err);
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      widgetRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]); // Only recreate on symbol change, not on height change

  const baseSymbol = symbol.replace(/USDT$|USDC$|USD$/i, '');

  // Resize handlers
  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current) return;
    const delta = e.clientY - startY.current;
    const newHeight = Math.min(
      window.innerHeight * 0.9,  // max 90vh
      Math.max(300, startHeight.current + delta)  // min 300px
    );
    setChartHeight(newHeight);
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    // Trigger resize for TradingView to adjust
    window.dispatchEvent(new Event('resize'));
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    setIsDragging(true);
    startY.current = e.clientY;
    startHeight.current = chartHeight;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [chartHeight, handleMouseMove, handleMouseUp]);

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <section className="tradingview-chart-section">
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="card-title mb-0">
              📊 Live Chart
              <span className="chart-symbol-display">{baseSymbol}/USDT</span>
            </div>
            <div className="chart-header-buttons">
              <button className="btn btn-sm close-chart-btn" onClick={onClose}>
                ✕ Close
              </button>
            </div>
          </div>
          <div className="card-body chart-body" style={{ height: chartHeight }}>
            {isDragging && <div className="chart-resize-overlay" />}
            <div
              id="tradingview_widget_container"
              ref={containerRef}
              className="tradingview-widget"
              style={{ height: chartHeight }}
            />
          </div>
          <div className="chart-resize-handle" onMouseDown={handleMouseDown}>
            <span className="resize-grip">⋯</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TradingViewChart;
